import { Center, Flex, Stack, Tag, Tooltip, Text } from '@chakra-ui/react'
import { StudyData } from '../types/FirestoreUser'
import { getStudyStatusColorScheme } from '../utils/studyHelpers'

interface StudiesListProps {
  /** Array of studies the user is participating in */
  studies?: (string | StudyData)[]
}

/**
 * Displays a vertical list of studies the user is participating in with color-coding and tooltips
 */
const StudiesList = ({ studies }: StudiesListProps) => {
  if (!studies || studies.length === 0) return null

  return (
    <Stack spacing={1}>
      {studies.map((study, index) => {
        // Handle both string and StudyData formats
        if (typeof study === 'string') {
          // If it's just a string (old format), show it as current (green) with basic tooltip that includes start date
          return (
            <Tooltip
              key={index}
              label={
                <Stack spacing={1} p={1}>
                  <Text>
                    <strong>Status:</strong> Participant
                  </Text>
                  <Text>
                    <strong>Start Date:</strong> Unknown
                  </Text>
                </Stack>
              }
              hasArrow
              placement="top"
              closeOnClick={false}
              openDelay={300}
            >
              <Tag
                size="md"
                borderRadius="full"
                variant="solid"
                colorScheme="green"
                width="90px"
              >
                <Flex justify="space-between" width="100%">
                  <Center>{study}</Center>
                </Flex>
              </Tag>
            </Tooltip>
          )
        } else {
          // If it's a StudyData object (new format), apply color-coding and detailed tooltip
          const studyName = study.name
          const status = study.status || 'Participant'

          const colorScheme = getStudyStatusColorScheme(status)

          // Create tooltip content with better formatting
          // Always include start date information (use "Unknown" as fallback)
          const tooltipContent = (
            <Stack spacing={1} p={1}>
              <Text>
                <strong>Status:</strong> {status}
              </Text>
              <Text>
                <strong>Start Date:</strong> {study.startDate || 'Unknown'}
              </Text>
              {study.endDate && (
                <Text>
                  <strong>End Date:</strong> {study.endDate}
                </Text>
              )}
              {study.reason && (
                <Text>
                  <strong>Reason:</strong> {study.reason}
                </Text>
              )}
            </Stack>
          )

          return (
            <Tooltip
              key={index}
              label={tooltipContent}
              hasArrow
              placement="top"
              closeOnClick={false}
              openDelay={300}
            >
              <Tag
                size="md"
                borderRadius="full"
                variant="solid"
                colorScheme={colorScheme}
                width="90px"
              >
                <Flex justify="space-between" width="100%">
                  <Center>{studyName}</Center>
                </Flex>
              </Tag>
            </Tooltip>
          )
        }
      })}
    </Stack>
  )
}

export default StudiesList
